import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Phone, PhoneIncoming, PhoneOutgoing, ChevronDown, ChevronUp,
  Loader2, Repeat,
} from 'lucide-react';
import WonPill from '../../components/WonPill';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: '2-digit', month: 'short', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(iso).slice(0, 16); }
};

const _fmtDuration = (sec) => {
  if (!sec) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}m ${s.toString().padStart(2, '0')}s`;
};

const _outcomeColor = (t) => {
  if (t === 'Closed Deal') return { bg: '#D1FAE5', fg: '#065F46' };
  if (t === 'Revivable Deal') return { bg: '#FEF3C7', fg: '#92400E' };
  if (t === 'Lost Deal') return { bg: '#FEE2E2', fg: '#991B1B' };
  return { bg: '#E2E8F0', fg: '#475569' };
};

/**
 * RelatedCallsCard — every other call between the same caller phone and
 * this domain. Rendered on the Call Detail page below the transcript so a
 * manager (or an agent picking up a reassigned deal) can scan the full
 * conversation history before dialling back.
 */
const RelatedCallsCard = ({ callId, phone }) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!callId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/calls/${callId}/related-calls?limit=50`);
        if (!cancelled) setRows(r.data?.related_calls || []);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [callId]);

  const other = useMemo(() => rows.filter((r) => !r.is_this_call), [rows]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading related calls…
        </div>
      </div>
    );
  }

  if (other.length === 0) return null; // Only show when there's history to see.

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="related-calls-card">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
        <div className="flex items-center gap-2">
          <Repeat className="w-4 h-4 text-teal-600" />
          <div>
            <p className="font-bold text-slate-900 text-sm" style={{ fontFamily: 'Outfit' }}>
              All calls with this number
            </p>
            <p className="text-[11px] text-slate-500 font-mono">{phone || '—'}</p>
          </div>
        </div>
        <span className="text-[11px] font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2 py-0.5">
          {other.length} other {other.length === 1 ? 'call' : 'calls'}
        </span>
      </div>
      <ul className="divide-y divide-slate-100">
        {other.map((r) => {
          const isExpanded = !!expanded[r.call_id];
          const oc = _outcomeColor(r.outcome_type);
          const DirectionIcon = r.call_direction === 'inbound' ? PhoneIncoming
            : r.call_direction === 'outbound' ? PhoneOutgoing : Phone;
          return (
            <li key={r.call_id} className="px-4 py-3 hover:bg-slate-50/60" data-testid={`related-call-${r.call_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2 min-w-0 flex-1">
                  <DirectionIcon className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-slate-800">{r.agent_name || '—'}</span>
                      <span className="text-[10px] text-slate-400">·</span>
                      <span className="text-[11px] text-slate-500">{_fmtDate(r.call_date)}</span>
                      <span className="text-[10px] text-slate-400">·</span>
                      <span className="text-[11px] text-slate-500 tabular-nums">{_fmtDuration(r.duration_seconds)}</span>
                      {r.overall_score != null && (
                        <span
                          className="text-[10px] font-bold rounded px-1.5 py-[1px]"
                          style={{
                            background: r.overall_score >= 70 ? '#D1FAE5' : r.overall_score >= 50 ? '#FEF3C7' : '#FEE2E2',
                            color: r.overall_score >= 70 ? '#065F46' : r.overall_score >= 50 ? '#92400E' : '#991B1B',
                          }}
                        >{Math.round(r.overall_score)}%</span>
                      )}
                      {r.outcome_type && (
                        <span
                          className="text-[10px] font-semibold rounded-full px-2 py-[1px]"
                          style={{ background: oc.bg, color: oc.fg }}
                        >{r.outcome_type}</span>
                      )}
                      {r.contacted_at && (
                        <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 rounded px-1.5 py-[1px]">
                          ✓ contacted
                        </span>
                      )}
                      {r.chase_outcome && (
                        <span
                          className="text-[10px] font-semibold rounded px-1.5 py-[1px]"
                          style={{
                            background: r.chase_outcome === 'won' ? '#D1FAE5' : '#FEE2E2',
                            color: r.chase_outcome === 'won' ? '#065F46' : '#991B1B',
                          }}
                        >chase {r.chase_outcome}</span>
                      )}
                      {(r.outcome_type === 'Closed Deal' || r.chase_outcome === 'won') && (
                        <WonPill source={r.chase_outcome_source} by={r.chase_outcome_by} />
                      )}
                    </div>
                    {isExpanded && r.summary && (
                      <p className="text-[11px] text-slate-600 mt-1.5 leading-relaxed whitespace-pre-wrap">
                        {r.summary}
                      </p>
                    )}
                    {!isExpanded && r.summary && (
                      <p className="text-[11px] text-slate-500 mt-1 line-clamp-1">{r.summary}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => setExpanded((p) => ({ ...p, [r.call_id]: !p[r.call_id] }))}
                    className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 inline-flex items-center gap-0.5"
                    data-testid={`related-toggle-${r.call_id}`}
                  >
                    {isExpanded ? <><ChevronUp className="w-3 h-3" /> less</>
                                 : <><ChevronDown className="w-3 h-3" /> more</>}
                  </button>
                  <Link
                    to={`/calls/${r.call_id}`}
                    className="text-[11px] font-bold text-teal-700 hover:text-teal-900 border border-teal-200 hover:bg-teal-50 rounded-md px-2 py-1"
                    data-testid={`related-open-${r.call_id}`}
                  >
                    Open →
                  </Link>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default RelatedCallsCard;
